import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {notify} from "../utils/toast";
import {get} from "aws-amplify/api";
import {Card, CardHeader, CardBody, CardTitle, Row, Col, Table, Form, FormGroup, Input, Button} from "reactstrap";
import {RingLoader} from "react-spinners";
import PageHeader from "../components/Common/PageHeader";
import ReactGA from "react-ga4";
import {GoogleAnalyticsEventCategories} from "../constants";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import "./GpuProfiles.scss";

interface GpuProfileWithVersion {
  gpu: string;
  gameClientVersion: string;
  platforms: string[];
}

interface GpuProfilesApiResponse {
  ultra?: GpuProfileWithVersion[];
  high?: GpuProfileWithVersion[];
  medium?: GpuProfileWithVersion[];
  basic?: GpuProfileWithVersion[];
  timestamp?: string;
  error?: {
    code: string;
    message: string;
    statusCode: number;
  };
}

const PROFILE_ORDER = ["ultra", "high", "medium", "basic"] as const;

const GpuProfiles = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<GpuProfilesApiResponse>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchGpuProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await get({
        apiName: "data",
        path: "/gpus/profiles"
      }).response;

      const responseData = (await response.body.json()) as GpuProfilesApiResponse;

      // Check if response contains error
      if (responseData.error) {
        const errorMessage = responseData.error.message;
        setError(errorMessage);
        notify("Failed to load GPU profiles", "danger");
      } else {
        setProfileData(responseData);

        // Calculate total GPUs for analytics
        const totalGpus =
          (responseData.ultra?.length || 0) +
          (responseData.high?.length || 0) +
          (responseData.medium?.length || 0) +
          (responseData.basic?.length || 0);

        ReactGA.event({
          category: GoogleAnalyticsEventCategories.BENCHMARKS,
          action: "GPU Profiles Loaded",
          value: totalGpus
        });
      }
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load GPU profiles";
      setError(errorMessage);
      notify(errorMessage, "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGpuProfiles();
  }, [fetchGpuProfiles]);

  const getProfileColor = (profile: string): string => {
    switch (profile.toLowerCase()) {
      case "ultra":
        return "danger";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "basic":
        return "success";
      default:
        return "secondary";
    }
  };

  const getProfileLabel = (profile: string): string => {
    return profile.charAt(0).toUpperCase() + profile.slice(1);
  };

  const handleGpuClick = (gpu: string): void => {
    navigate(`/search/${encodeURIComponent(gpu)}`);
  };

  const downloadCsv = (profileKey: string, gpus: GpuProfileWithVersion[]): void => {
    // Generate CSV content
    const headers = ["GPU", "Platform", "Game Client Version (earliest recorded)"];
    const csvRows = [headers.join(",")];

    gpus.forEach((gpu) => {
      const row = [
        `"${gpu.gpu.replace(/"/g, '""')}"`, // Escape quotes in GPU name
        `"${gpu.platforms?.join(", ") || "Unknown"}"`,
        `"${gpu.gameClientVersion}"`
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `gpu-profiles-${profileKey.toLowerCase()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    ReactGA.event({
      category: GoogleAnalyticsEventCategories.BENCHMARKS,
      action: "CSV Downloaded",
      label: profileKey
    });
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) {
      return text;
    }

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, idx) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={idx} className="gpu-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const filterGpus = (gpus: GpuProfileWithVersion[] | undefined): GpuProfileWithVersion[] => {
    if (!gpus || !searchQuery.trim()) {
      return gpus || [];
    }

    const queryLower = searchQuery.toLowerCase();
    return gpus.filter((gpu) => gpu.gpu.toLowerCase().includes(queryLower));
  };

  const renderProfileTable = (profileKey: string, themeColor: string) => {
    const allGpus = profileData[profileKey as keyof GpuProfilesApiResponse] as GpuProfileWithVersion[] | undefined;
    const filteredGpus = filterGpus(allGpus);

    if (!allGpus || allGpus.length === 0) {
      return null;
    }

    // Don't render the table if search is active but no matches
    if (searchQuery.trim() && filteredGpus.length === 0) {
      return null;
    }

    const profileLabel = getProfileLabel(profileKey);
    const cardColor = getProfileColor(profileKey);

    return (
      <Col md="6" key={profileKey} className="mb-4">
        <Card>
          <CardHeader className={`bg-${cardColor} text-white d-flex justify-content-between align-items-center`}>
            <CardTitle tag="h5" className="mb-0">
              {profileLabel} ({filteredGpus.length} GPU{filteredGpus.length !== 1 ? "s" : ""})
            </CardTitle>
            <Button
              color="primary"
              data-color={themeColor}
              size="sm"
              onClick={() => downloadCsv(profileKey, filteredGpus)}
              className="csv-download-btn"
            >
              <i className="nc-icon nc-cloud-download-93 mr-1" />
              CSV
            </Button>
          </CardHeader>
          <CardBody>
            <div className="table-responsive">
              <Table hover className="gpu-profiles-table">
                <thead>
                  <tr>
                    <th className="gpu-col">GPU</th>
                    <th className="platform-col">Platform</th>
                    <th className="version-col">Game Client Version (earliest recorded)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGpus.map((gpu, idx) => (
                    <tr key={`${gpu.gpu}-${idx}`}>
                      <td className="gpu-name gpu-col">
                        <a
                          href="#"
                          className="gpu-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handleGpuClick(gpu.gpu);
                          }}
                        >
                          {highlightMatch(gpu.gpu, searchQuery)}
                        </a>
                      </td>
                      <td className="platform-cell platform-col">{gpu.platforms?.join(", ") || "Unknown"}</td>
                      <td className="version-cell version-col">{gpu.gameClientVersion}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <PageHeader title="GPU to Profile Lookup" subtitle="GPU to profile mappings by Zwift game client version" />
          <div className="gpu-profiles-description">
            <p>
              Profile defines your graphics quality settings: visual fidelity (polygon density, shadow detail, texture
              resolution, etc.), rendering distance, and effect complexity. Zwift's engine automatically assigns your
              profile based on your GPU's capabilities - you cannot override it manually. Profile is independent of
              resolution. The following tables map each GPU to its assigned profile, based on anonymized performance
              data collected from user logs since 2016. Click on a GPU to see its performance data.
            </p>
          </div>
          {!loading && !error && (
            <Row className="mb-4">
              <Col md="12">
                <Card>
                  <CardBody>
                    <Form>
                      <FormGroup className="mb-3">
                        <Input
                          placeholder="🔍 Search GPUs (e.g., RTX 3060, 1650, M1)..."
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </FormGroup>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          {loading && (
            <div className="iconWrapper">
              <RingLoader color="#4682b4" size={80} loading={true} />
            </div>
          )}
          {!loading && error && (
            <Row>
              <Col md="12">
                <Card>
                  <CardBody>
                    <div className="alert alert-danger" role="alert">
                      <strong>Error:</strong> {error}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          {!loading && !error && (
            <Row>
              {PROFILE_ORDER.map((profileKey) => renderProfileTable(profileKey, color)).filter(
                (element) => element !== null
              )}
            </Row>
          )}
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default GpuProfiles;
